import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Plane } from "lucide-react";

export default function RecruitSignup() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const pollRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (pollRef.current >= 6) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (pollRef.current >= 6) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", {
        ...form,
        role: "recruit",
        session_id: sessionId,
      });
      login(data.token, data.user);
      toast.success("License granted. Welcome aboard.");
      navigate("/handshake");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B14] flex items-center justify-center p-6">
      <div className="absolute top-6 left-6">
        <Link to="/" data-testid="recruit-signup-home-link"><Logo /></Link>
      </div>

      <div className="w-full max-w-lg" data-testid="recruit-signup-page">
        {status === "checking" && (
          <div className="glass-strong p-10 rounded-sm text-center">
            <div className="w-16 h-16 mx-auto mb-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            <div className="hud-label mb-3">Verifying Payment</div>
            <p className="text-slate-400 text-sm">Hold tight, pilot. We're confirming your license with Stripe.</p>
          </div>
        )}

        {status === "failed" && (
          <div className="glass-strong p-10 rounded-sm">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={24} className="text-[#F43F5E]" />
              <div className="hud-label text-[#F43F5E]">Payment Not Verified</div>
            </div>
            <p className="text-slate-300 mb-6">
              We couldn't confirm your payment. If you completed checkout, please refresh.
              Otherwise, restart from the landing page.
            </p>
            <Link
              to="/"
              data-testid="recruit-signup-back-home-link"
              className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-sm"
            >
              Return to Briefing
            </Link>
          </div>
        )}

        {status === "paid" && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 size={20} className="text-[#34D399]" />
              <span className="hud-label text-[#34D399]">Payment Confirmed</span>
            </div>

            <h1 className="font-display text-3xl sm:text-4xl text-white font-light mb-4">
              Claim your <span className="text-gold italic">Pilot License</span>
            </h1>
            <p className="text-slate-400 mb-8 text-sm">
              Last step — create your recruit credentials and the Architect will brief you.
            </p>

            <form onSubmit={submit} className="space-y-5 glass-strong p-8 rounded-sm" data-testid="recruit-signup-form">
              <Field label="Full Name" value={form.full_name} onChange={(v) => upd("full_name", v)} testId="recruit-name-input" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => upd("email", v)} testId="recruit-email-input" />
              <Field label="Password" type="password" value={form.password} onChange={(v) => upd("password", v)} testId="recruit-password-input" />

              <button
                type="submit"
                disabled={loading}
                data-testid="recruit-signup-submit-button"
                className="btn-gold w-full py-3.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Plane size={16} />
                {loading ? "Activating..." : "Activate License"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, testId }) => (
  <label className="block">
    <div className="hud-label text-[10px] mb-2">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      required
      className="w-full bg-[#0A1122] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-gold transition-colors"
    />
  </label>
);
